import { IDashboardVehicle } from './../../../shared/models/dashboard.model';
import { Component, Input, OnDestroy, OnInit, signal } from '@angular/core';
import { Router } from '@angular/router';
import { combineLatest, Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import * as fromDashboard from './../../../store/bdcdashboard/bdcdashboard.selectors';
import { Store } from '@ngrx/store';
import { BdcDashboardState } from './../../../store/bdcdashboard/bdcdashboard.reducer';
import { CancelAppointmentFromBdcDashboard } from './../../../store/bdcdashboard/bdcdashboard.actions';
import { AppointmentType, IVehicle } from '@signal/asp-data-commons';
import { DateTime } from 'luxon';
import { unsubscribeSubscriptions} from '../../../shared/services/util.service';
import { ServiceAppointmentState } from '../../../store/service-appointment/service-appointment.reducer';
import { SetMakeModelYrPageDefault } from '../../../store/service-appointment/service-appointment.actions';
import { TranslateService } from '@ngx-translate/core';
import { SubscriptionList } from '../../../../modules/shared/models/asp.types';
import * as util from '../../../shared/services/util.service';
import { VehiclesState } from '../../../store/vehicle/vehicle.reducer';
import { selectAllVehiclesFromInventory, selectVehiclesLoaded } from '../../../store/vehicle/vehicle.selectors';

@Component({
  selector: 'app-appointment-section',
  templateUrl: './appointment-section.component.html',
  styleUrls: ['./appointment-section.component.scss'],
})
export class AppointmentSectionComponent implements OnInit, OnDestroy {
  static readonly CONFIG = {
    "default": {
      title: 'service.myVehicles',
      singleNotificationTxt: (_count) => `service.youHaveVehicle`,
      multipleNotificationTxt: (_count) => `service.youHaveVehicles`,
      noNotificationTxt: `service.youHaveNoVehicles`,
      bookNewAppointmentTxt: 'testDrive.selectDifferentVehicle',
      loadingTxt:'service.loadingMyvehicles',
    },
    "all": {
      title: 'service.myAppointments',
      singleNotificationTxt: (_count) => `service.youHavAppointment`,
      multipleNotificationTxt: (_count) => `service.youHavAppointments`,
      noNotificationTxt: `service.youHaveNoAppointments`,
      bookNewAppointmentTxt: 'testDrive.allAppointments',
      loadingTxt:'service.loadingAllAppointments',
    }
  };
  @Input() appointmentType: AppointmentType | string;
  isTestDrive: boolean;
  isService: boolean;
  isDelivery: boolean;
  isLearning: boolean;
  isVehicleInquiry: boolean;
  isThisLastSection: boolean;
  appointments$ : Observable<any>;
  /* Observables for vehicles */
  salesOrServiceAppointmentVehicles$: Observable<IDashboardVehicle[]>;
  salesOrServiceAppointmentCount$: Observable<number>;
  isUserVehiclesLoading$: Observable<boolean>;
  isVehiclesLoading$: Observable<boolean>;
  showLoader=false;
  isMyVehicle:boolean = false;
  userVehicles$: Observable<IVehicle[]>;
  allInventoryVehicles: any[] = [];
  isOwnVehicleHasAppt:boolean = false;
  subscriptions: SubscriptionList = {};
  readonly panelOpenState = signal(false);
  readonly isPartsEnabled = util.environment.enableParts && util.isPartsAccessible;
  
  constructor(
    private readonly router: Router,
    private readonly dashboardStore: Store<BdcDashboardState>,
    private readonly vehicleState: Store<VehiclesState>,
    private readonly serviceAppointmentStore: Store<ServiceAppointmentState>,
    private readonly translate : TranslateService
  ) {
  }

  ngOnInit(): void {
      this.isVehiclesLoading$ = this.vehicleState.select(selectVehiclesLoaded);
      this.subscriptions.inventoryVehicles = combineLatest([
        this.vehicleState.select(selectVehiclesLoaded),
        this.vehicleState.select(selectAllVehiclesFromInventory)
      ]).subscribe(([loaded, allVehicles]) => {
        if (loaded && allVehicles) {
          this.allInventoryVehicles = allVehicles?.length ? allVehicles : [];
        }
      });
     if(this.appointmentType === 'all') {
          this.salesOrServiceAppointmentVehicles$ = this.dashboardStore.select(fromDashboard.selectAllApptForDashboard)
            .pipe(map((vehicles: IDashboardVehicle[]) =>
              (vehicles || []).filter(vehicle => this.isPartsEnabled || !this.isPartsPickupAppointmentType(vehicle))
            ));
          this.salesOrServiceAppointmentCount$ = this.salesOrServiceAppointmentVehicles$
            .pipe(map((vehicles: IDashboardVehicle[]) => vehicles.length));
      }
      else {
      this.showLoader = true;
        this.userVehicles$ = this.dashboardStore.select(fromDashboard.selectMyUserVehicles);
        this.salesOrServiceAppointmentCount$ = this.dashboardStore.select(fromDashboard.selectMyUserVehiclesCount);
        this.subscriptions.vehicleLoading = this.dashboardStore.select(fromDashboard.selectUserVehiclesLoading).subscribe(value=>{
          if(!value){
            this.showLoader = false;
          }
        })
        this.isMyVehicle = true;
        this.isVehiclesLoading$ = of(true);
      } 
  }

  isPartsPickupAppointmentType(vehicle: IDashboardVehicle): boolean {
    const appointmentType = vehicle?.appointment?.appointmentType;
    if (!appointmentType || typeof appointmentType !== 'string') {
      return false;
    }

    return appointmentType.toUpperCase().includes('PART');
  }

  sortAppointments(vehicles) : Array<any>{
    vehicles.forEach( vehicle => {
      if(vehicle.appointment.length>1){
        vehicle.appointment.sort((appt1,appt2)=>{
          const startDate1 = DateTime.fromFormat(`${appt1.appointmentStartDate} , ${appt1.timeSlotId}`,'yyyy-MM-dd , t');
          const startDate2 = DateTime.fromFormat(`${appt2.appointmentStartDate} , ${appt2.timeSlotId}`,'yyyy-MM-dd , t');
          return startDate1.toMillis() - startDate2.toMillis();
        })
      }
    });
    return vehicles;
  }

  onNewAppointmentClick() {
    switch (this.appointmentType) {
      case AppointmentType.TEST_DRIVE:
        this.router.navigate(['/asp/bdc/testdrive']);
        break;
      case AppointmentType.DELIVERY:
        this.router.navigate(['/asp/bdc/delivery']);
        break;
      case AppointmentType.LEARNING:
        this.router.navigate(['/asp/bdc/tech-appointment']);
        break;
      case AppointmentType.VEHICLE_INQUIRY:
        this.router.navigate(['/asp/bdc/vehicleinquiry']);
        break;
      case AppointmentType.SERVICE:
        this.router.navigate(['/asp/bdc/service']);
        break;
      default :
      this.serviceAppointmentStore.dispatch(new SetMakeModelYrPageDefault());
        this.router.navigate(['/asp/bdc/service']);
        break;
    }
  }

  onServiceAppointmentCancel(vehicle: IDashboardVehicle,index:number) {
    this.dashboardStore.dispatch(
      new CancelAppointmentFromBdcDashboard({
        appointment: vehicle.appointment[index]
      }));
  }
  onSalesAppointmentCancel(vehicle: IDashboardVehicle) {
    this.dashboardStore.dispatch(
      new CancelAppointmentFromBdcDashboard({
        appointment: vehicle.appointment
      }));
  }

  getTitle(): string {
    return this.translate.instant(this.getConfig()?.title);
  }

  getLoadingTxt(): string {
    return this.translate.instant(this.getConfig()?.loadingTxt);
  }

  isButtonDisable() {
    if(this.appointmentType === AppointmentType.SERVICE || this.appointmentType === 'default') {
      return !util.isServiceAccessibleFeatureFlag && !!util.isSpServiceDashboardOverride;
    }
    else if(this.appointmentType === AppointmentType.LEARNING || this.appointmentType === AppointmentType.TEST_DRIVE || this.appointmentType === AppointmentType.DELIVERY || this.appointmentType === AppointmentType.VEHICLE_INQUIRY) {
        return !util.isSalesAccessibleFeatureFlag;
    }
    else {
      return false;
    }
  }

  getConfig() {
    return AppointmentSectionComponent.CONFIG[this.appointmentType];
  }

  ngOnDestroy() {
    unsubscribeSubscriptions(this.subscriptions);
  }
}
